import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateAnswer,
  addNewItem,
  removeItem,
  updateSectionItems,
} from "../../../redux/slices/careplanSlice";
import {
  fetchCarePlanInstance,
  saveCarePlanData,
  unlockCarePlan,
} from "../../../redux/actions/careplanActions";
import { useDashboardNav } from "../../../hooks/useDashboardNav";
// import CarePlanFormLayout from "../../common/CarePlanFormLayout";
import styled from "styled-components";
import UrgentCare from "./UrgentCare";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import AddItemOverlay from "../CarePlanBuilder/AddItemOverlay";
import MultiSelectDropdown from "./MultiSelectDropdown";
import {
  CircleFilled,
  CircleUnfilled,
  PlusIcon,
  DeleteIcon,
} from "../../common/Icons";
import IconButton from "../../common/Button";
import TextField from "../../common/TextField";
import Spacer from "../../common/Spacer";
import ItemThumbnail from "../../common/ItemThumbnail";
import DropIndicator from "../../common/DropIndicator";
import NavigationButtons from "./NavigationButtons";

const EditCarePlan = ({ onClose, nhsNumber, carePlanId }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const go = useDashboardNav();

  const { items, carePlan, instanceLoading } = useSelector(
    (state) => state.careplan
  );

  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [showFinishConfirmModal, setShowFinishConfirmModal] = useState(false);
  const [showSavedConfirmation, setShowSavedConfirmation] = useState(false);
  const [savedToDb, setSavedToDb] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [selectedItemIdsPerSection, setSelectedItemIdsPerSection] = useState(
    {}
  );
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [newItemTitle, setNewItemTitle] = useState("");
  const [newItemDescription, setNewItemDescription] = useState("");
  const [showAddItemOverlay, setShowAddItemOverlay] = useState(false);
  const [isAddOtherItemOpen, setAddOtherItemOpen] = useState(false);
  const [draggingItemId, setDraggingItemId] = useState(null);
  const [deleteText, setDeleteText] = useState("");

  // Unlock the care plan when the component is unmounted via the browser back button or closing the tab or window
  useEffect(() => {
    const handleUnlock = async () => {
      await dispatch(unlockCarePlan(carePlan.id));
    };

    const handleBeforeUnload = (event) => {
      handleUnlock();
      event.preventDefault();
      event.returnValue = ""; // Required for Chrome
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handleUnlock);
    window.addEventListener("popstate", handleUnlock);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handleUnlock);
      window.removeEventListener("popstate", handleUnlock);
    };
  }, [carePlan.id, dispatch]);

  // Memoize 'sections' to prevent unnecessary re-renders
  const carePlanSections = useMemo(
    () => carePlan.sections || [],
    [carePlan.sections]
  );
  // Filter out the NHS Number section as it is not needed to show in edit
  const filteredSections = useMemo(
    () => carePlanSections.filter((section) => section.id !== 1),
    [carePlanSections]
  );

  const currentSection = filteredSections[activeSection] || {};
  const currentSectionId = currentSection.id;
  const isDraggable = currentSection.isServiceUserSpecific;

  useEffect(() => {
    formRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [activeSection]);

  useEffect(() => {
    const sectionId = filteredSections[activeSection]?.id;
    if (sectionId) {
      const currentSelectedIds =
        filteredSections[activeSection].items?.map((item) => item.id) || [];
      setSelectedItemIdsPerSection((prevState) => ({
        ...prevState,
        [sectionId]: currentSelectedIds,
      }));
    }
  }, [activeSection, filteredSections]);

  useEffect(() => {
    if (!carePlan?.id || carePlan.id !== carePlanId) {
      dispatch(fetchCarePlanInstance(carePlanId));
    }
  }, [carePlanId, carePlan?.id, dispatch]);

  const handleClose = () => {
    setShowCloseConfirmation(true);
  };

  const handleCloseConfirmation = (confirm) => {
    if (confirm) {
      dispatch(unlockCarePlan(carePlan.id)).then(() => {
        // Option 1: Manually update Redux state
        dispatch({
          type: "careplan/updateCarePlanLockStatus",
          payload: { id: carePlan.id, is_locked: false, locked_by: null },
        });
        onClose(); // Call the onClose prop to close the modal
      });
    } else {
      setShowCloseConfirmation(false);
    }
  };

  const handleSave = () => {
    setShowFinishConfirmModal(true);
  };

  const handleFinishConfirmation = (confirm) => {
    if (confirm) {
      dispatch(saveCarePlanData({ isEdit: true })).then(() => {
        // Refresh care plan data
        dispatch(fetchCarePlanInstance(carePlan.id)).then(() => {
          setSavedToDb(true);
          setShowFinishConfirmModal(false);
          setShowSavedConfirmation(true);
          // Wait until unlock is successful, then refresh the draft list
          dispatch(unlockCarePlan(carePlan.id))
            .then(() => {
              // Option 1: Manually update Redux state
              dispatch({
                type: "careplan/updateCarePlanLockStatus",
                payload: { id: carePlan.id, is_locked: false, locked_by: null },
              });
            })
            .catch((err) => {
              console.error("Unlock failed", err);
            });
        });
      });
    } else {
      setShowFinishConfirmModal(false);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    const form = document.getElementById("deleteConfirmationForm"); // Select the form
    const deleteTextField = document.querySelector("input[name=deleteText]");
    // Check if the field is empty
    if (!deleteTextField.value.trim()) {
      deleteTextField.setCustomValidity("This field is required."); // Show empty field message
    } else if (deleteTextField.value !== "Remove") {
      deleteTextField.setCustomValidity(
        'Please type "Remove" to confirm deletion.'
      ); // Show custom message
    } else {
      deleteTextField.setCustomValidity(""); // Clear the custom validation message
    }

    // Trigger form validation
    if (form && !form.reportValidity()) {
      return; // Stop if the form is invalid
    }
    dispatch(
      removeItem({
        sectionId: filteredSections[activeSection].id,
        itemId: itemToDelete.id,
      })
    );
    setShowDeleteConfirmModal(false);
    setItemToDelete(null);
    setDeleteText("");
  };

  const handleAddNewItem = () => {
    // if (newItemTitle.trim() === "" || newItemDescription.trim() === "") {
    if (newItemTitle.trim() === "") {
      // Need to shwo an error
      return; // Do not add an empty item
    }

    const newItem = {
      id: `new-${Date.now()}`,
      title: newItemTitle,
      description: newItemDescription,
      answer: "",
    };

    dispatch(
      addNewItem({
        sectionId: filteredSections[activeSection].id,
        item: newItem,
      })
    );

    setNewItemTitle("");
    setNewItemDescription("");
    setAddOtherItemOpen(false);
  };

  const handleUpdateSelectedItems = (updatedSelectedItemIds) => {
    const sectionId = filteredSections[activeSection].id;
    const existingItems = filteredSections[activeSection].items || [];
    const existingItemIds = existingItems.map((i) => i.id);

    // 1) Figuring out which IDs are truly new (i.e. weren’t already in the section)
    const newItemIds = updatedSelectedItemIds.filter(
      (id) => !existingItemIds.includes(id)
    );

    if (newItemIds.length) {
      // 2) Grab their full item objects, sort them by title, then dispatch addNewItem in that order
      const newItems = items
        .filter((item) => newItemIds.includes(item.id))
        .sort((a, b) => a.title.localeCompare(b.title));

      newItems.forEach((item) => {
        dispatch(
          addNewItem({
            sectionId,
            item: { ...item, fromOverlay: true },
          })
        );
      });
    }

    // 2) Remove only those overlay‑added items that were deselected
    existingItems.forEach((existing) => {
      if (
        existing.fromOverlay &&
        !updatedSelectedItemIds.includes(existing.id)
      ) {
        dispatch(removeItem({ sectionId, itemId: existing.id }));
      }
    });

    // 3) Update the UI’s selected‑IDs map
    setSelectedItemIdsPerSection((prev) => ({
      ...prev,
      [sectionId]: updatedSelectedItemIds,
    }));
  };

  const sectionLabelMapping = {
    2: "What my well looks like",
    3: "Things to help me stay well",
    4: "Symptoms I’m getting unwell",
    5: "Things to help me get better",
    6: "Urgent care",
  };

  const handleDragStart = (e, itemId) => {
    setDraggingItemId(itemId); // Set the dragging item ID
    e.dataTransfer.setData("text/plain", itemId); // Required for Firefox
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Allow dropping
  };

  const handleDrop = (e, targetItemId) => {
    e.preventDefault();

    e.currentTarget.style.opacity = "0"; // Reset the opacity of the drop indicator

    if (draggingItemId === targetItemId) {
      setDraggingItemId(null); // Clear the dragging state even if dropped on itself
      return;
    }

    const sectionId = filteredSections[activeSection].id;
    const sectionIndex = filteredSections.findIndex(
      (section) => section.id === sectionId
    );

    if (sectionIndex === -1) return;

    const updatedItems = [...filteredSections[sectionIndex].items];

    const fromIndex = updatedItems.findIndex(
      (item) => item.id === draggingItemId
    );
    const toIndex = targetItemId
      ? updatedItems.findIndex((item) => item.id === targetItemId)
      : updatedItems.length;

    if (fromIndex === -1 || toIndex === -1) return;

    // Move the item
    const [draggedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, draggedItem);

    // Update the order property
    const reorderedItems = updatedItems.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    // Dispatch an action to update the section items in the Redux store
    dispatch(
      updateSectionItems({
        sectionId,
        items: reorderedItems,
      })
    );

    setDraggingItemId(null); // Reset the dragging item ID after drop
  };

  const handleDropIndicatorDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.style.opacity = "1";
  };

  const handleDropIndicatorDragLeave = (e) => {
    e.currentTarget.style.opacity = "0";
  };

  // Define Back and Continue handlers (as in CreateCarePlan):
  const handleContinue = () => {
    if (activeSection < carePlan.sections.length - 1) {
      setActiveSection(activeSection + 1);
    } else {
      // Last section reached: prompt finish confirmation
      setShowFinishConfirmModal(true);
    }
  };

  const handleBack = () => {
    if (activeSection > 0) {
      setActiveSection(activeSection - 1);
    }
  };

  if (instanceLoading || !carePlan?.id || carePlan.id !== carePlanId) {
    return (
      <FullScreenOverlay title={`Edit Care Plan: ${nhsNumber}`}>
        <div style={{ padding: 24 }}>Loading…</div>
      </FullScreenOverlay>
    );
  }

  return (
    <>
      <FullScreenOverlay
        activeSection={activeSection}
        title={`Edit Care Plan: ${nhsNumber}`}
        onClose={onClose}
        width="100%"
        height="100%"
        buttons={[
          {
            label: "Close",
            onClick: handleClose,
            variant: "secondary",
            state: "idle",
          },
          {
            label: "Save",
            onClick: handleSave,
            variant: "primary",
          },
        ]}
      >
        <MainContainer>
          <Sidebar>
            {/* ─────────── DESKTOP: full list ─────────── */}
            <DesktopOnlyWrapper>
              {filteredSections.map((section, index) => (
                <SidebarItem
                  key={section.id}
                  $active={activeSection === index}
                  onClick={() => setActiveSection(index)}
                >
                  {activeSection === index ? (
                    <CircleFilled color={section.color} />
                  ) : (
                    <CircleUnfilled />
                  )}
                  {sectionLabelMapping[section.id] || section.title}
                </SidebarItem>
              ))}
            </DesktopOnlyWrapper>

            {/* ─────────── MOBILE: arrows + single active ─────────── */}
            <MobileOnlyWrapper>
              <ArrowButton
                onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                disabled={activeSection === 0}
              >
                ↑
              </ArrowButton>

              <SidebarItem $active={true} onClick={() => {}}>
                {sectionLabelMapping[filteredSections[activeSection].id] ||
                  filteredSections[activeSection].title}
              </SidebarItem>

              <ArrowButton
                onClick={() =>
                  setActiveSection(
                    Math.min(filteredSections.length - 1, activeSection + 1)
                  )
                }
                disabled={activeSection === filteredSections.length - 1}
              >
                ↓
              </ArrowButton>
            </MobileOnlyWrapper>
          </Sidebar>
          <FormWrapper ref={formRef}>
            <FormContainer>
              <FormTitleWrapper>
                <Rectangle color={filteredSections[activeSection]?.color} />
                <h5 className="semi-bold neutral-900">
                  {filteredSections[activeSection]?.title}
                </h5>
              </FormTitleWrapper>

              {filteredSections[activeSection]?.id === 6 ? (
                // Contact Page Section
                <UrgentCare
                  retrievedContacts={carePlan.contacts}
                  initialImportantInfo={carePlan.important_info}
                />
              ) : (
                // Other Sections
                <>
                  {filteredSections[activeSection]?.items?.length > 0 ? (
                    filteredSections[activeSection].items.map(
                      (item, index, array) => (
                        <React.Fragment
                          key={`${filteredSections[activeSection].id}-${item.id}`}
                        >
                          {filteredSections[activeSection]
                            ?.isServiceUserSpecific && (
                            <DropIndicator
                              beforeItemId={item.id}
                              onDragOver={handleDropIndicatorDragOver}
                              onDragLeave={handleDropIndicatorDragLeave}
                              onDrop={(e) => handleDrop(e, item.id)}
                              margin="-26px"
                            />
                          )}

                          <ItemContainer
                            draggable={isDraggable}
                            $isDragging={draggingItemId === item.id}
                            onDragStart={
                              isDraggable
                                ? (e) => handleDragStart(e, item.id)
                                : undefined
                            }
                            onDragOver={
                              isDraggable ? handleDragOver : undefined
                            }
                            onDragEnd={() => setDraggingItemId(null)}
                            onDrop={
                              isDraggable
                                ? (e) => handleDrop(e, item.id)
                                : undefined
                            }
                          >
                            {/* Render “Remove” whenever this is a user-added item */}
                            {(currentSection.isServiceUserSpecific ||
                              String(item.id).startsWith("new-") ||
                              item.user_defined ||
                              item.fromOverlay ||
                              item.is_overlay_selected) && (
                              <IconButtonWrapper
                                style={{
                                  position: "absolute",
                                  top: "16px",
                                  right: "16px",
                                  zIndex: 10,
                                }}
                                onClick={() => handleDeleteClick(item)}
                              >
                                <IconButton
                                  variant="ghost"
                                  isRemove={true}
                                  noPadding={true}
                                  icon={<DeleteIcon color="#EC2D30" />}
                                >
                                  Remove
                                </IconButton>
                              </IconButtonWrapper>
                            )}
                            <ItemTitle>
                              <h6 className="semi-bold neutral-900">
                                {item.title}
                              </h6>
                            </ItemTitle>
                            <ItemInnerContainer>
                              <ItemThumbnailContainer>
                                <ItemThumbnail
                                  src={item?.image_url}
                                  alt={item?.image_alt_text || item?.title}
                                  width={"150px"}
                                  height={"150px"}
                                  border={"1px solid #e9ecef"}
                                />
                              </ItemThumbnailContainer>
                              <ItemDetailContainer>
                                <MultiSelectDropdown
                                  prompt_text={
                                    item.section_prompt_overrides?.[
                                      currentSectionId
                                    ] ?? item.prompt_text
                                  }
                                  options={
                                    item.section_overrides?.[
                                      currentSectionId
                                    ] ??
                                    item.standard_answers ??
                                    []
                                  }
                                  value={item.answer || ""}
                                  onChange={(newValue) => {
                                    dispatch(
                                      updateAnswer({
                                        sectionId:
                                          filteredSections[activeSection].id,
                                        itemId: item.id,
                                        answer: newValue,
                                      })
                                    );
                                  }}
                                />
                              </ItemDetailContainer>
                            </ItemInnerContainer>
                          </ItemContainer>
                          {index === array.length - 1 &&
                            filteredSections[activeSection]
                              ?.isServiceUserSpecific && (
                              <DropIndicator
                                beforeItemId={null}
                                onDragOver={handleDropIndicatorDragOver}
                                onDragLeave={handleDropIndicatorDragLeave}
                                onDrop={(e) => handleDrop(e, null)}
                                margin="-26px"
                              />
                            )}
                        </React.Fragment>
                      )
                    )
                  ) : (
                    <EmptyItemContainer>
                      <p className="body-text-regular regular neutral-600">
                        This topic doesn’t have any pre-defined sections. Start
                        adding from the default list or create user-specific
                        sections.
                      </p>
                      <IconButton
                        variant="primary"
                        state="idle"
                        width={"250px"}
                        type="button"
                        onClick={() => setShowAddItemOverlay(true)}
                      >
                        Select item from list
                      </IconButton>
                      <IconButton
                        variant="ghost"
                        state="idle"
                        icon={<PlusIcon color="#004F96" />}
                        iconPosition="left"
                        type="button"
                        onClick={() => setAddOtherItemOpen(true)}
                      >
                        Add other item
                      </IconButton>
                    </EmptyItemContainer>
                  )}
                </>
              )}
              {filteredSections[activeSection]?.items?.length > 0 &&
              filteredSections[activeSection]?.isServiceUserSpecific ? (
                <EmptyItemContainer>
                  <IconButton
                    variant="primary"
                    state="idle"
                    width={"250px"}
                    type="button"
                    onClick={() => setShowAddItemOverlay(true)}
                  >
                    Select item from list
                  </IconButton>
                  <IconButton
                    variant="ghost"
                    state="idle"
                    icon={<PlusIcon color="#004F96" />}
                    iconPosition="left"
                    type="button"
                    onClick={() => setAddOtherItemOpen(true)}
                  >
                    Add other item
                  </IconButton>
                </EmptyItemContainer>
              ) : (
                filteredSections[activeSection]?.items?.length > 0 && (
                  <IconButton
                    variant="ghost"
                    state="idle"
                    width={"210px"}
                    icon={<PlusIcon color="#004F96" />}
                    iconPosition="left"
                    type="button"
                    onClick={() => setAddOtherItemOpen(true)}
                  >
                    Add other item
                  </IconButton>
                )
              )}
            </FormContainer>
            {/* Render navigation buttons for back/continue/finish */}
            <NavigationButtons
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              totalSections={carePlan.sections.length - 1}
              handleContinue={handleContinue}
              handleBack={handleBack}
              handleFinish={() => setShowFinishConfirmModal(true)}
            />
          </FormWrapper>
        </MainContainer>

        {/* Modals and Overlays */}
        {isAddOtherItemOpen && (
          <FullScreenOverlay
            iconClose={true}
            width="60%"
            height="370px"
            title="Add other item"
            isSlimModal={true}
            onClose={() => setAddOtherItemOpen(false)}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault(); // Prevent default form submission behavior
                handleAddNewItem();
              }}
            >
              <NewItemPopupContent>
                <p className="body-text-regular semi-bold neutral-800">
                  Item title
                </p>
                <p className="body-text-regular regular neutral-700">
                  Give the item a clear and descriptive title so users know what
                  it is about
                </p>
                <TextInputWrapper>
                  <TextField
                    placeholder="Enter your title"
                    maxLength={100}
                    showCharacterLimit={true}
                    name="title"
                    value={newItemTitle}
                    onChange={(e) => setNewItemTitle(e.target.value)}
                  />
                </TextInputWrapper>
              </NewItemPopupContent>
              <Spacer height="24px" />
              <NewItemPopupContent>
                <Spacer height="24px" />
                <ButtonGroup>
                  <IconButton
                    variant="secondary"
                    onClick={() => setAddOtherItemOpen(false)}
                    state="idle"
                    width={"50%"}
                  >
                    Cancel
                  </IconButton>
                  <IconButton
                    variant="primary"
                    onClick={handleAddNewItem}
                    state="idle"
                    width={"50%"}
                  >
                    Add to Care Plan
                  </IconButton>
                </ButtonGroup>
              </NewItemPopupContent>
            </form>
          </FullScreenOverlay>
        )}

        {showDeleteConfirmModal && itemToDelete && (
          <FullScreenOverlay
            width="20%"
            height="20%"
            isPopup={true}
            popupType="delete"
            buttons={[
              {
                label: "No, Cancel",
                onClick: () => {
                  setShowDeleteConfirmModal(false);
                  setItemToDelete(null);
                  setDeleteText("");
                },
              },
              {
                label: "Yes, Remove",
                onClick: handleConfirmDelete,
                variant: "danger",
                state: "idle",
              },
            ]}
          >
            <h5 className="semi-bold">Confirm remove item</h5>
            <p>
              Are you sure you want to remove the item{" "}
              <span className="confirm-delete-nhs">{itemToDelete.title}</span>{" "}
              from this care plan? This action cannot be undone and will be
              permanently removed.
            </p>
            <p>
              Type <b>"Remove"</b> to confirm
            </p>
            <form
              id="deleteConfirmationForm"
              onSubmit={(e) => e.preventDefault()}
            >
              <TextField
                placeholder=""
                maxLength={10}
                showCharacterLimit={false}
                name="deleteText"
                value={deleteText}
                onChange={(e) => {
                  setDeleteText(e.target.value);
                  e.target.setCustomValidity(""); // Clear custom validation message
                }}
                autoComplete="off" // otherwise the Delete will show in autocomplete which is not the good behaviour here
                required
              />
            </form>
          </FullScreenOverlay>
        )}

        {showAddItemOverlay && (
          <AddItemOverlay
            allItems={items}
            selectedItemIds={
              selectedItemIdsPerSection[filteredSections[activeSection].id] ||
              []
            }
            onClose={() => setShowAddItemOverlay(false)}
            onUpdateSelectedItems={handleUpdateSelectedItems}
            addButtonText="Add to Care Plan"
          />
        )}
      </FullScreenOverlay>
      {showCloseConfirmation && (
        <FullScreenOverlay
          width="20%"
          height="20%"
          isPopup={true}
          popupType="confirmation"
          buttons={[
            {
              label: "No",
              onClick: () => handleCloseConfirmation(false),
            },
            {
              label: "Yes",
              onClick: () => handleCloseConfirmation(true),
              variant: "primary",
            },
          ]}
        >
          <h5 className="semi-bold">Are you sure you want to close?</h5>
          <p>You will lose any unsaved changes.</p>
        </FullScreenOverlay>
      )}

      {showFinishConfirmModal && (
        <FullScreenOverlay
          width="524px"
          height="225px"
          isSlimModal={true}
          isPopup={true}
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => handleFinishConfirmation(false),
            },
            {
              label: "Yes, Save",
              onClick: () => handleFinishConfirmation(true),
              variant: "primary",
              state: "idle",
              type: "button",
            },
          ]}
        >
          <h5 className="semi-bold">Save changes?</h5>
          <p>
            By proceeding, this Care Plan will be updated. You can access and
            edit it under the Draft Care Plans list page.
          </p>
        </FullScreenOverlay>
      )}

      {showSavedConfirmation && (
        <FullScreenOverlay
          width="20%"
          height="20%"
          isPopup={true}
          popupType="save"
          buttons={[
            {
              label: "Overview",
              onClick: () => {
                onClose();
                go("overview");
              }, // Redirect to Dashboard Overview
              variant: "primary",
            },
            {
              label: "Draft plans",
              onClick: () => {
                setShowSavedConfirmation(false);
                // Taking user to the Draft Care Plans page
                onClose();
                go("draft-care-plans");
              },
            },
          ]}
        >
          <h5 className="semi-bold">Care Plan Saved</h5>
          {savedToDb ? (
            <p>
              You’ve successfully saved the Care Plan {nhsNumber}. You can view
              it under Draft Care Plans.
            </p>
          ) : (
            <p>
              You’ve successfully saved the Care Plan {nhsNumber}. You can
              access it once you navigate to New Care Plan next time.
            </p>
          )}
        </FullScreenOverlay>
      )}
    </>
  );
};

export default EditCarePlan;

// ... Styled components remain the same ...

const TextInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

// const TextAreaWrapper = styled.div`
//   display: flex;
//   flex-direction: column;
//   width: 100%;
// `;

const ButtonGroup = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
`;

const IconButtonWrapper = styled.div``;

const MainContainer = styled.div`
  display: flex;
  gap: 24px;
  padding: 0px 40px;
  height: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0px 16px;
  }
`;

const Sidebar = styled.div`
  width: 20%;
  min-width: 312px;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  position: sticky;
  top: 0;

  @media (max-width: 768px) {
    // flex-direction: row; /* or whatever you need on mobile */
    width: 100%;
    min-width: auto;
    margin-bottom: 16px;
    /* display: none; Hide sidebar on mobile */
    flex-direction: column;
    align-items: center;
    gap: 0;
  }

  @media (max-width: 768px) {
    .desktop-list {
      display: none;
    }
  }

  @media (min-width: 769px) {
    .mobile-only-wrapper {
      display: none;
    }
  }
`;

const SidebarItem = styled.div`
  display: flex;
  padding: 16px;
  align-items: center;
  gap: 8px;
  width: 88%;
  border-radius: 10px;
  font-size: 16px;
  color: ${({ $active }) => ($active ? "#161718" : "#6C757D")};
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  line-height: 20px;
  border: 1px solid ${({ $active }) => ($active ? "transparent" : "#e9ecef")};
  cursor: pointer;
  box-shadow: ${({ $active }) =>
    $active ? "0px 5px 15px 0px rgba(0, 0, 0, 0.1)" : "none"};

  //   &:hover {
  //     background-color: #f1f5f9;
  //     color: #004f96;
  //   }
`;

const FormContainer = styled.div`
  display: flex;
  flex: 1;
  padding: 24px;
  flex-direction: column;
  //   justify-content: center;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  background: #fff;
  box-shadow: 0px 10px 16px 0px rgba(230, 230, 230, 0.25);
`;

const FormWrapper = styled.form`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  max-height: 100%;
`;

const FormTitleWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
`;

const Rectangle = styled.div`
  flex: 1 0 0;
  align-self: stretch;
  border-radius: 100px;
  background: ${({ color }) => color || "#3a70e2"};
  width: 4px;
  max-width: 4px;
  max-height: 32px;
`;

const ItemContainer = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  background: #f8f9fa;

  position: relative;

  /* drag cursor */
  cursor: ${({ draggable }) => (draggable ? "grab" : "default")};
  &:active {
    cursor: grabbing;
  }

  /* dynamic opacity when dragging */
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};

  /* keep real buttons clickable */
  & *:not(button):not(input):not(textarea) {
    cursor: inherit;
  }
`;

const ItemTitle = styled.div`
  display: flex;
  height: 24px;
  align-items: center;
  justify-content: space-between;
  align-self: stretch;
`;

const ItemInnerContainer = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 12px;
  background: #fff;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ItemDetailContainer = styled.div`
  display: flex;
  padding: 16px 16px 16px 8px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;
`;

const ItemThumbnailContainer = styled.div`
  margin: 16px;
`;

const NewItemPopupContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const EmptyItemContainer = styled.div`
  display: flex;
  padding: 50px 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  align-self: stretch;
  border-radius: 12px;
  border: 1px dashed #ced4da;

  p {
    max-width: 529px;
    text-align: center;
  }
`;

const ArrowButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 12px;
  width: 100%;
  text-align: center;
  color: #6c757d;

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  @media (min-width: 769px) {
    display: none; /* hide arrows on desktop */
  }
`;

const MobileOnlyWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
`;

const DesktopOnlyWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    display: none;
  }
`;
